import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

import { ref, computed, watch, onBeforeUnmount, onMounted } from 'vue'
import mapboxgl, {
  ExpressionSpecification,
  FillExtrusionLayerSpecification,
  GeoJSONSourceSpecification
} from 'mapbox-gl'
import GeojsonLayer, { GeojsonLayerEvent } from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import MapPopupNew from '@/components/map/popups/MapPopupNew.vue'
import { useGeneralStore } from '@/pinia'
import { CHANGE_LEVEL, CHANGE_BUILDING } from '@/store/action-types'
import { PLANVIEW_PREFIX } from '@/store/module-prefixes'
import { formatNumber } from '@/utils/math'
import { SET_CLICKED_ITEM_TYPE } from '@/store/mutation-types'
import { setViewToIndoorview, setViewToPlanview } from '@/utils/views'
import { getLevelsForBuilding } from '@/services/floors.service'
import { LEVEL, LEVEL_PROPERTIES } from '@/interfaces/level.interface'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { BUILDING_PROPERTIES } from '@/interfaces/building.interface'
import useMapbox, { zoomToEntities } from '@/composables/useMapbox'
import { Row } from '@/components/Builder/BuilderComponent.vue'
import useColoredEntities, { ColoredEntityData } from '@/composables/useColoredEntities'
import { RGBA } from '@/utils/rgba'
import { configToMapboxExpression, getColorForValue } from '@/utils/colorScale'
import {
  createColorCircleLabel,
  createDivider,
  createPrimaryButton,
  createSmallSubtitle,
  createSmallTitle,
  createTinyBoldText
} from '@/components/Builder'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { storeToRefs } from 'pinia'
import { useEntityLevel } from '@/composables/useEntityLevel'

const LAYER_LEVEL_BUILDING_ID = 'layer-level-building'
const LAYER_LEVEL_BUILDING_SOURCE_ID = 'sourceLevelBuilding'
const LAYER_LEVEL_ID = 'layer-level'
const LAYER_LEVEL_SOURCE_ID = 'sourceLevel'

type Props = {
  before: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LayerLevel',
  props: {
    before: {}
  },
  setup(__props: any) {



const filters = useCurrentFeatureFilters()
const store = useStore()
const { t } = useI18n()
const { map } = useMapbox()
const piniaStore = useGeneralStore()
const { setShowFeature } = useGeneralStore()

const popupHoveredHtml = ref<InstanceType<typeof MapPopupNew> | null>(null)
const popupSelectedHtml = ref<InstanceType<typeof MapPopupNew> | null>(null)

const popupHover = new mapboxgl.Popup({ closeButton: false, offset: 5 })
const popupSelected = new mapboxgl.Popup({ closeButton: false, offset: 5 })

const color = ref('rgb(153,153,153)')
const colorHovered = ref('rgb(99,99,99)')
const colorSelected = ref('rgb(76,76,76)')
const colorSelectedItem = ref('rgba(250, 219, 20, 0.8)')
const coloredEntities = useColoredEntities()
const hoverFloor = ref<LEVEL_PROPERTIES | null>(null)
const hoveredLevelId = ref(null)
const multipleBuildingsLevel = ref<LEVEL[]>([])
const hoveredBuildingName = ref('')
const { enabledEntityLevels } = storeToRefs(useEntityLevel())
const levelIsEnabled = computed(() => enabledEntityLevels.value.includes('LEVEL'))

const buildingSource = computed(() => store.state.buildingSource)
const clickedItemType = computed(() => store.state.clickedItemType)
const currentDataComparison = computed(() => store.state.currentDataComparison)
const dataComparisonInterval = computed(() => store.state.dataComparisonInterval)
const levelsForSelectedBuilding = computed(() => store.state.levelsForSelectedBuilding)
const previousView = computed(() => store.state.previousView)
const selectedLevel = computed(() => store.state.selectedLevel)
const hasIndoorviewAccess = computed(() => store.getters.hasIndoorviewAccess)
const isDataComparisonToolActive = computed(() => store.getters.isDataComparisonToolActive)
const isMapviewShowing = computed(() => store.getters.isMapviewShowing)
const isMeasurementActive = computed(() => store.getters.isMeasurementActive)
const selectedLevelNumber = computed(() => store.getters.selectedLevelNumber)
const selectedLevelId = computed<string | null>(() => store.getters.selectedLevelId ?? null)
const allBuildings = computed(() => store.getters.allBuildings)
const selectedBuilding = computed(() => store.state.mapview.selectedBuilding)
const selectedBuildingId = computed<string | null>(
  () => store.getters['mapview/selectedBuildingId'] ?? null
)
const selectedBuildingName = computed<string | null>(
  () => store.getters['mapview/selectedBuildingName'] ?? null
)
const selectedBuildingsDashboard = computed<BUILDING_PROPERTIES[]>(
  () => filters.value?.submitted?.buildings || []
)
const selectedAttachmentEntityId = computed<string>(() => {
  return piniaStore.selectedAttachment?.entityId ? piniaStore.selectedAttachment.entityId : ''
})
const selectedTicketEntityId = computed<string>(() => {
  return piniaStore.selectedTicket?.entityConnection?.length &&
    piniaStore.selectedTicket?.entityConnection[0].id
    ? piniaStore.selectedTicket.entityConnection[0].id
    : ''
})
const isItemEntityBuilding = computed<boolean>(() => {
  if (
    !selectedBuildingId.value ||
    (!selectedAttachmentEntityId.value && !selectedTicketEntityId.value)
  )
    return false
  return (
    selectedBuildingId.value === selectedAttachmentEntityId.value ||
    selectedBuildingId.value === selectedTicketEntityId.value
  )
})
const dataComparisonType = computed<string>(() => {
  switch (currentDataComparison.value) {
    case 'BTA':
      return 'bta'
    case 'BRA':
      return 'bra'
    case 'LOA-h':
      return 'loa'
    case t('general__data__area-efficiency-loa-h'):
      return 'areaEfficiency'
    default:
      return 'bta'
  }
})

const isLevelSourceAvailable = computed<boolean>(() => {
  // @ts-ignore
  return layerLevelSource.value && layerLevelSource.value.data.features.length
})

const layerLevelBuildingSource = computed<GeoJSONSourceSpecification | null>(() => {
  if (multipleBuildingsLevel.value.length <= 1) {
    if (!selectedBuilding.value || !selectedBuilding.value.geometry || isLevelSourceAvailable.value)
      return null
  }
  const newSource: GeoJSONSourceSpecification = {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  }
  const currentBuilding = buildingSource.value.data.features.find(
    (building) => building.id === selectedBuilding.value.id
  )
  if (multipleBuildingsLevel.value.length > 1) {
    const buildingNoLevel = selectedBuildingsDashboard.value.filter((building) => {
      return !multipleBuildingsLevel.value.some(
        (level) => building.id === level.properties.constructionId
      )
    })
    // @ts-ignore
    newSource.data.features.push(...buildingNoLevel)
  } else {
    // @ts-ignore
    newSource.data.features.push(currentBuilding)
  }
  return newSource
})

const layerLevelBuildingStyle = computed<FillExtrusionLayerSpecification>(() => {
  return {
    id: LAYER_LEVEL_BUILDING_ID,
    source: LAYER_LEVEL_BUILDING_SOURCE_ID,
    type: 'fill-extrusion',
    layout: {
      visibility: isMapviewShowing.value && !isLevelSourceAvailable.value ? 'visible' : 'none'
    },
    paint: {
      'fill-extrusion-color': fillExtrusionColorBuilding.value,
      'fill-extrusion-height': ['get', 'height'],
      'fill-extrusion-base': 0,
      'fill-extrusion-opacity': 0.9,
      'fill-extrusion-vertical-gradient': false
    }
  }
})
const coloredBuildingsExpression = computed((): ExpressionSpecification => {
  const coloredBuildingsExpression: ExpressionSpecification = configToMapboxExpression(
    'coloredBuildingValue',
    coloredEntities.colorScale,
    new RGBA(29, 35, 79, 1)
  )

  return [
    'case',
    ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
    [
      'case',
      ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
      coloredBuildingsExpression,
      fallbackExpression.value
    ],
    fallbackExpression.value
  ]
})
const fallbackExpression = computed((): ExpressionSpecification => {
  return [
    'case',
    ['==', ['get', 'id'], selectedLevelId.value],
    isDataComparisonToolActive.value ? colorSelected.value : '#03071F',
    ['==', ['get', 'id'], hoveredLevelId.value],
    isDataComparisonToolActive.value ? colorHovered.value : '#0a103b',
    isDataComparisonToolActive.value ? color.value : '#1d234f'
  ]
})
const fillExtrusionColorBuilding = computed(() => {
  if (!multipleBuildingsLevel.value.length) {
    return coloredBuildingsExpression.value
  }
  return fallbackExpression.value
})

const layerLevelSource = computed<GeoJSONSourceSpecification | null>(() => {
  if (!levelsForSelectedBuilding.value || selectedBuildingsDashboard.value.length > 1) return null
  const levelSource: GeoJSONSourceSpecification = {
    type: 'geojson',
    promoteId: 'id',
    data: {
      // @ts-ignore
      features: levelsForSelectedBuilding.value.data.features.map((level) => {
        const levelProperties = level.properties as LEVEL_PROPERTIES
        const value = coloredEntities.data?.find(
          (entity: ColoredEntityData) => entity.id === levelProperties.id
        )?.value
        return {
          ...level,
          properties: {
            ...levelProperties,
            coloredLevelValue: value
          }
        }
      }),
      type: 'FeatureCollection'
    }
  }
  return levelSource
})

const layerMultipleLevelSource = computed<GeoJSONSourceSpecification>(() => {
  const levelSource: GeoJSONSourceSpecification = {
    type: 'geojson',
    promoteId: 'id',
    data: {
      // @ts-ignore
      features: multipleBuildingsLevel.value.map((level) => {
        const levelProperties = level.properties as LEVEL_PROPERTIES
        const value = coloredEntities.data?.find(
          (entity: ColoredEntityData) => entity.id === levelProperties.id
        )?.value
        return {
          ...level,
          properties: {
            ...levelProperties,
            coloredLevelValue: value
          }
        }
      }),
      id: LAYER_LEVEL_SOURCE_ID,
      type: 'FeatureCollection'
    }
  }
  return levelSource
})
const fillExtrusionColor = computed(() => {
  if (coloredEntities.active) {
    return fillExtrusionColorLevelExpression.value
  } else {
    return fillExtrusionExpression.value
  }
})

const fillExtrusionColorLevelExpression = computed((): ExpressionSpecification => {
  const coloredLevels = configToMapboxExpression(
    'coloredLevelValue',
    coloredEntities.colorScale,
    fallbackExpression.value
  )
  return [
    'case',
    ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
    ['feature-state', 'color'],
    [
      'case',
      ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
      ['feature-state', 'color'],
      coloredLevels // Fallback to defaultExpression if feature-state color is not set
    ]
  ]
})

const fillExtrusionExpression = computed<ExpressionSpecification>(() => {
  const defaultExpression: ExpressionSpecification = [
    'case',
    ['==', ['get', 'id'], selectedAttachmentEntityId.value],
    colorSelectedItem.value,
    ['==', ['get', 'id'], selectedTicketEntityId.value],
    colorSelectedItem.value,
    ['==', ['get', 'id'], selectedLevelId.value],
    isDataComparisonToolActive.value ? colorSelected.value : '#03071F',
    ['==', ['get', 'id'], hoveredLevelId.value],
    isDataComparisonToolActive.value ? colorHovered.value : '#0a103b',
    isDataComparisonToolActive.value
      ? color.value
      : isItemEntityBuilding.value
      ? colorSelectedItem.value
      : '#1d234f'
  ]
  return [
    'case',
    ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
    ['feature-state', 'color'],
    [
      'case',
      ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
      ['feature-state', 'color'],
      defaultExpression // Fallback to defaultExpression if feature-state color is not set
    ]
  ]
})

watch(fillExtrusionExpression, () => {
  if (!map) return
  if (!map.getLayer(LAYER_LEVEL_ID)) return
  if (coloredEntities.active) return
  map.setPaintProperty(LAYER_LEVEL_ID, 'fill-extrusion-color', fillExtrusionExpression.value)
})

const layerLevelStyle = computed<FillExtrusionLayerSpecification>(() => {
  return {
    id: LAYER_LEVEL_ID,
    source: LAYER_LEVEL_SOURCE_ID,
    type: 'fill-extrusion',
    layout: {
      visibility: isMapviewShowing.value ? 'visible' : 'none'
    },
    paint: {
      'fill-extrusion-color': fillExtrusionColor.value,
      'fill-extrusion-height': ['get', 'height'],
      'fill-extrusion-base': ['get', 'elevation'],
      'fill-extrusion-opacity': 0.9,
      'fill-extrusion-vertical-gradient': false
    },
    minzoom: 10
  }
})
const hoveredLevelIdMachingColor = computed(
  () => coloredEntities.data?.find((v) => v.id === hoveredLevelId.value)?.value
)
const colorLabel = computed<string | undefined>(() =>
  hoveredLevelIdMachingColor.value !== undefined
    ? coloredEntities.title +
      ': ' +
      coloredEntities.formatter(hoveredLevelIdMachingColor.value) +
      coloredEntities.unit
    : undefined
)
const colorColoredEntiteties = computed(() =>
  getColorForValue(coloredEntities.colorScale, hoveredLevelIdMachingColor.value ?? 0)
)
const colorMetaKeyToLabel = computed<Record<string, string>>(() => ({
  attractiveness: t('general__data__attractiveness'),
  condition: t('general__data__condition'),
  area: t('general__data__area'),
  numberOfSpaces: t('general__data__number-of-spaces')
}))
const colorMetaKeyToFormatter = {
  attractiveness: (x) => x.toString(),
  condition: (x) => x.toString(),
  area: (x) => `${formatNumber(x, 0)} m²`,
  numberOfSpaces: (x) => x.toString()
}
const colorMeta = computed<Record<string, any>>(
  () =>
    coloredEntities.data?.find((v) => v.id === hoveredLevelId.value)?.meta ??
    ({} as Record<string, any>)
)
const createColorMetaRows = (colorMeta: Record<string, any>): Row[] => {
  return Object.entries(colorMeta).map(([key, value]) => {
    const label = colorMetaKeyToLabel.value[key] ? colorMetaKeyToLabel.value[key] : t(key)
    const text = colorMetaKeyToFormatter[key] ? colorMetaKeyToFormatter[key](value) : value
    return createTinyBoldText(label + ': ' + text, {
      style: {
        marginTop: '4px'
      }
    })
  })
}
const rows = computed<Row[]>(() => [
  createSmallTitle(
    multipleBuildingsLevel.value.length > 1
      ? hoveredBuildingName.value
      : selectedBuildingName.value ?? '?'
  ),
  createSmallSubtitle(popupSubtitle.value ? popupSubtitle.value : popupLevel.value),
  ...(colorLabel.value
    ? [
        createDivider(),
        createColorCircleLabel(colorColoredEntiteties.value.toHex(), colorLabel.value),
        ...(coloredEntities.active ? createColorMetaRows(colorMeta.value) : [])
      ]
    : [])
])
const popupButtons = computed(() => {
  const header: Row[] = [
    createSmallTitle(
      multipleBuildingsLevel.value.length > 1
        ? hoveredBuildingName.value
        : selectedBuildingName.value ?? '?'
    ),
    createSmallSubtitle(popupLevel.value),
    ...(colorLabel.value
      ? [
          createDivider(),
          createColorCircleLabel(colorColoredEntiteties.value.toHex(), colorLabel.value),
          ...(coloredEntities.active ? createColorMetaRows(colorMeta.value) : [])
        ]
      : [])
  ]
  if (selectedLevel.value && selectedLevel.value.geometryFromBuilding) return header
  const buttons: Row[] = [createDivider()]
  if (selectedLevel.value) {
    buttons.push(createPrimaryButton(t('general__view__planview'), 'clickPlanview', -1))
  }
  if (selectedBuilding.value?.navvisLinks?.length && hasIndoorviewAccess.value) {
    buttons.push(createPrimaryButton(t('general__view__indoorview'), 'clickIndoorview', -1))
  }
  return header.concat(buttons)
})

const popupLevel = computed(() => {
  return selectedLevelNumber.value
    ? t('general__text__level') + ' ' + selectedLevelNumber.value
    : ''
})

const popupSubtitle = computed(() => {
  return hoverFloor.value && hoverFloor.value.level
    ? t('general__text__level') + ' ' + hoverFloor.value.level
    : t('general__data__data-missing-level-info')
})

watch(
  selectedBuildingsDashboard,
  async () => {
    await updateLayerMultipleLevel()
  },
  { immediate: true }
)

watch([dataComparisonInterval, dataComparisonType, selectedBuilding], () =>
  setDataComparisonColors(dataComparisonType.value)
)
onMounted(() => setDataComparisonColors(dataComparisonType.value))

onBeforeUnmount(() => {
  popupHover.remove()
  popupSelected.remove()
})

async function handleClick(event: GeojsonLayerEvent) {
  if (isMeasurementActive.value || !isFirstLayer(event)) return
  const features = event.features
  if (!features || !features.length || !features[0].properties?.level) return
  if (selectedBuildingsDashboard.value.length > 1) {
    const building = allBuildings.value.find(
      (building: any) => building.id === features[0].properties?.constructionId
    )
    await store.dispatch(CHANGE_BUILDING, { building })
    store.commit(SET_CLICKED_ITEM_TYPE, { type: 'building' })
  }
  store.dispatch(CHANGE_LEVEL, { levelNumber: features[0].properties.level })
  store.commit(SET_CLICKED_ITEM_TYPE, { type: 'level' })
  showPopup(event, 'selected')
}

function handleClickOnIndoorviewButton() {
  setShowFeature('featureInfo')
  setViewToIndoorview()
}

async function handleClickOnPlanviewButton() {
  setViewToPlanview()
  if (previousView.value !== 'dashboard') {
    zoomToEntities([selectedBuilding.value.id])
  }
  await store.dispatch(PLANVIEW_PREFIX + '/loadSensorData')
}

function handleMouseEnter() {
  if (isMeasurementActive.value) return
  setCursor('pointer')
}

function handleMouseLeave() {
  if (isMeasurementActive.value) return
  setCursor('')
  popupHover.remove()
  hoveredLevelId.value = null
  hoverFloor.value = null
}

function handleMouseMove(event: GeojsonLayerEvent) {
  if (isMeasurementActive.value || !isFirstLayer(event)) return
  updateHoverLevel(event)
}

function isFirstLayer(event: GeojsonLayerEvent) {
  if (!map) return
  const renderedFeatures = map.queryRenderedFeatures(event.point)
  if (renderedFeatures[0]?.layer?.id === 'layer-marker-building') return
  return (
    (renderedFeatures?.length && renderedFeatures[0].layer?.id === LAYER_LEVEL_ID) ||
    renderedFeatures[0]?.layer?.id === LAYER_LEVEL_BUILDING_ID
  )
}

function setCursor(cursorType: string) {
  if (map) map.getCanvas().style.cursor = cursorType
}

function setDataComparisonColors(dataComparisonType: string) {
  if (!selectedBuilding.value[dataComparisonType]) {
    color.value = 'rgb(153,153,153)'
    colorHovered.value = 'rgb(99,99,99)'
    colorSelected.value = 'rgb(76,76,76)'
  } else if (
    selectedBuilding.value[dataComparisonType] >= dataComparisonInterval.value.low &&
    selectedBuilding.value[dataComparisonType] <= dataComparisonInterval.value.high
  ) {
    color.value = 'rgb(51,201,100)'
    colorHovered.value = 'rgb(32,123,61)'
    colorSelected.value = 'rgb(21,82,40)'
  } else if (selectedBuilding.value[dataComparisonType] < dataComparisonInterval.value.low) {
    color.value = 'rgb(95,152,179)'
    colorHovered.value = 'rgb(61,101,118)'
    colorSelected.value = 'rgb(42,70,82)'
  } else {
    color.value = 'rgb(161,80,80)'
    colorHovered.value = 'rgb(118,59,59)'
    colorSelected.value = 'rgb(82,42,42)'
  }
}

function showPopup(event: GeojsonLayerEvent, type: string) {
  if (!popupHoveredHtml.value || !popupSelectedHtml.value) return
  if (type === 'hover') {
    if (map) popupHover.setLngLat(event.lngLat).setDOMContent(popupHoveredHtml.value.$el).addTo(map)
    popupHover.trackPointer()
  } else if (type === 'selected') {
    if (map)
      popupSelected.setLngLat(event.lngLat).setDOMContent(popupSelectedHtml.value.$el).addTo(map)
  }
}

function updateHoverLevel(event: GeojsonLayerEvent) {
  const features = event.features
  if (!features || !features.length) {
    popupHover.remove()
    hoverFloor.value = null
    return
  }
  if (hoverFloor.value && features[0].properties?.id === hoverFloor.value.id) return
  hoveredLevelId.value = features[0].properties?.id
  if (selectedBuildingsDashboard.value.length > 1) {
    const hoveredBuilding = selectedBuildingsDashboard.value.find(
      (building: any) => building.id === features[0].properties?.constructionId
    )
    if (hoveredBuilding) hoveredBuildingName.value = hoveredBuilding.displayName
    if (!hoveredBuilding) hoveredBuildingName.value = features[0].properties?.displayName
  }
  popupSelected.remove()
  showPopup(event, 'hover')
  hoverFloor.value = features[0].properties as LEVEL_PROPERTIES
}

async function updateLayerMultipleLevel() {
  if (selectedBuildingsDashboard.value.length <= 1) {
    multipleBuildingsLevel.value = []
    return null
  }
  selectedBuildingsDashboard.value.forEach(async (building: any) => {
    const levelForBuilding = await getLevelsForBuilding(building.id, building.geometry, false)
    if (!levelForBuilding) return
    multipleBuildingsLevel.value.push(...levelForBuilding.data.features)
  })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (layerLevelBuildingSource.value)
      ? (_openBlock(), _createBlock(GeojsonLayer, {
          key: 0,
          before: _ctx.before,
          layer: layerLevelBuildingStyle.value,
          source: layerLevelBuildingSource.value,
          sourceId: LAYER_LEVEL_BUILDING_SOURCE_ID,
          onLayerMouseEnter: handleMouseEnter,
          onLayerMouseLeave: handleMouseLeave,
          onLayerMouseMove: handleMouseMove
        }, null, 8, ["before", "layer", "source"]))
      : _createCommentVNode("", true),
    (
        ((layerLevelSource.value && (clickedItemType.value === 'building' || clickedItemType.value === 'level')) ||
          multipleBuildingsLevel.value.length > 1) &&
        levelIsEnabled.value
      )
      ? (_openBlock(), _createBlock(GeojsonLayer, {
          key: 1,
          before: _ctx.before,
          layer: layerLevelStyle.value,
          layerId: LAYER_LEVEL_ID,
          source: multipleBuildingsLevel.value.length > 1 ? layerMultipleLevelSource.value : layerLevelSource.value,
          sourceId: LAYER_LEVEL_SOURCE_ID,
          onLayerClicked: handleClick,
          onLayerMouseEnter: handleMouseEnter,
          onLayerMouseLeave: handleMouseLeave,
          onLayerMouseMove: handleMouseMove
        }, null, 8, ["before", "layer", "source"]))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", null, [
      _withDirectives(_createVNode(MapPopupNew, {
        ref_key: "popupHoveredHtml",
        ref: popupHoveredHtml,
        title: 
          multipleBuildingsLevel.value.length > 1 ? hoveredBuildingName.value : selectedBuildingName.value ?? '?'
        ,
        subtitle: popupSubtitle.value,
        rows: rows.value
      }, null, 8, ["title", "subtitle", "rows"]), [
        [_vShow, selectedBuildingId.value || multipleBuildingsLevel.value.length > 1]
      ]),
      _withDirectives(_createVNode(MapPopupNew, {
        ref_key: "popupSelectedHtml",
        ref: popupSelectedHtml,
        title: 
          multipleBuildingsLevel.value.length > 1 ? hoveredBuildingName.value : selectedBuildingName.value ?? '?'
        ,
        subtitle: popupLevel.value,
        rows: popupButtons.value,
        onClickPlanview: handleClickOnPlanviewButton,
        onClickIndoorview: handleClickOnIndoorviewButton
      }, null, 8, ["title", "subtitle", "rows"]), [
        [_vShow, selectedBuildingId.value || multipleBuildingsLevel.value.length > 1]
      ])
    ], 512), [
      [_vShow, false]
    ])
  ]))
}
}

})